import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { pubStatusLabel } from "../lib/status-labels";
import { useQuery } from "@tanstack/react-query";
import { Search, AlertCircle, ImageOff, X } from "lucide-react";
import { api } from "../lib/api";
import type { SearchResponse, SearchResult } from "../lib/types";
import { PageHeader } from "../components/layout/PageHeader";
import { Card } from "../components/ui/card";
import { Badge, type BadgeVariant } from "../components/ui/badge";
import { ArchivedBadge } from "../components/ui/ArchivedBadge";
import { Skeleton } from "../components/ui/skeleton";
import { EmptyState } from "../components/ui/empty-state";
import { FilterPanel, type FilterGroup } from "../components/ui/FilterPanel";
import { Thumb } from "../components/ui/Thumb";

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  live: "live",
  draft: "draft",
  unpublished: "unpublished",
  error: "error",
};

const MATCH_FIELD_LABEL: Record<string, string> = {
  sku: "SKU",
  name: "Nome",
  description: "Descrição",
  tag: "Tag",
};

export function SearchPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const q = params.get("q")?.trim() ?? "";
  // Campo de busca da propria pagina, so abaixo de lg: o header mobile nao
  // tem busca, e a aba "Buscar" do TabBar caia num beco sem saida ("use a
  // busca no topo", que nao existe no celular). Prova em prod 23/09.
  const [draft, setDraft] = useState(q);
  const canalFiltro = params.get("canal");
  const statusFiltro = params.get("status");
  const ondeFiltro = params.get("onde");

  const { data, isLoading, isError, error } = useQuery<SearchResponse>({
    queryKey: ["search", q],
    queryFn: async () => {
      const r = await api.post<SearchResponse>("/api/search", { q });
      return r.data;
    },
    enabled: q.length > 0,
  });

  // Referencia estavel entre renders (useMemo) pra nao invalidar o useMemo
  // de `groups` abaixo a cada render com um array `?? []` novo.
  const allResults = useMemo(() => data?.results ?? [], [data]);

  // Contagens reais SOBRE o resultado desta busca (o backend nao expoe facets
  // pra /api/search — decisao local, KISS: contar os proprios resultados).
  const groups: FilterGroup[] = useMemo(() => {
    const byChannel = new Map<string, number>();
    const channelName = new Map<string, string>();
    const byStatus = new Map<string, number>();
    const byMatch = new Map<string, number>();
    for (const r of allResults) {
      byChannel.set(r.channel_slug, (byChannel.get(r.channel_slug) ?? 0) + 1);
      if (r.channel_name) channelName.set(r.channel_slug, r.channel_name);
      byStatus.set(r.status, (byStatus.get(r.status) ?? 0) + 1);
      byMatch.set(r.match_field, (byMatch.get(r.match_field) ?? 0) + 1);
    }
    return [
      {
        key: "canal",
        label: "Canal",
        options: [...byChannel.entries()].map(([value, count]) => ({ value, label: channelName.get(value) ?? value, count })),
      },
      {
        key: "status",
        label: "Status",
        options: [...byStatus.entries()].map(([value, count]) => ({
          value,
          label: pubStatusLabel(value),
          count,
        })),
      },
      {
        key: "onde",
        label: "Onde buscar",
        options: [...byMatch.entries()].map(([value, count]) => ({
          value,
          label: MATCH_FIELD_LABEL[value] ?? value,
          count,
        })),
      },
    ].filter((g) => g.options.length > 1);
  }, [allResults]);

  const results = allResults.filter((r) => {
    if (canalFiltro && r.channel_slug !== canalFiltro) return false;
    if (statusFiltro && r.status !== statusFiltro) return false;
    if (ondeFiltro && r.match_field !== ondeFiltro) return false;
    return true;
  });

  const filterValue = { canal: canalFiltro, status: statusFiltro, onde: ondeFiltro };
  const onFilterChange = (key: string, value: string | null): void => {
    const next = new URLSearchParams(params);
    if (value == null) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };
  const onFilterClear = (): void => {
    const next = new URLSearchParams(params);
    next.delete("canal");
    next.delete("status");
    next.delete("onde");
    setParams(next, { replace: true });
  };
  const clearTerm = (): void => {
    setParams(new URLSearchParams(), { replace: true });
  };

  return (
    <div>
      <PageHeader title="Resultados da busca" description="Em todos os canais que você tem acesso" />

      <form
        className="lg:hidden"
        role="search"
        style={{ marginBottom: "1rem" }}
        onSubmit={(e) => {
          e.preventDefault();
          const term = draft.trim();
          if (term) setParams({ q: term });
        }}
      >
        <div className="relative">
          <Search
            className="icon"
            aria-hidden
            style={{ position: "absolute", left: "0.75rem", top: "50%", transform: "translateY(-50%)", color: "var(--ink-3)" }}
          />
          <input
            type="search"
            className="input"
            style={{ paddingLeft: "2.5rem" }}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Buscar SKU, nome, tag… (todos os canais)"
            aria-label="Buscar SKU, nome ou tag em todos os canais"
            enterKeyHint="search"
          />
        </div>
      </form>

      {!q ? (
        <EmptyState
          icon={<Search className="h-12 w-12" />}
          title="Busque por SKU, nome ou tag"
          description="Digite acima (no celular) ou na busca do topo (no computador). A busca cobre nome, descrição e tag em todos os canais que você acessa."
        />
      ) : (
        <div className="flex flex-col lg:flex-row lg:items-start gap-4">
          {/* FilterPanel devolve DOIS elementos (aside desktop + gatilho mobile)
              via Fragment — nunca usar `.with-filters` (sempre row) aqui: no
              mobile o gatilho vira um segundo item de flex-row e espreme o
              corpo (bug medido: body ficava com metade da largura e a lista
              vazava horizontalmente). Column no mobile, row so a partir de lg. */}
          {groups.length > 0 ? (
            <FilterPanel groups={groups} value={filterValue} onChange={onFilterChange} onClear={onFilterClear} />
          ) : null}
          <div className="flex-1 min-w-0 flex flex-col gap-4">
            <div className="cluster">
              <button
                type="button"
                className="chip chip--coral"
                onClick={clearTerm}
                aria-label={`Limpar busca por "${q}"`}
                style={{ cursor: "pointer", border: "none", font: "inherit" }}
              >
                {q} <X style={{ width: "0.75rem", height: "0.75rem" }} aria-hidden />
              </button>
              {!isLoading && !isError ? (
                <span className="muted text-md">
                  {results.length} resultado{results.length === 1 ? "" : "s"}
                  {results.length !== allResults.length ? ` de ${allResults.length}` : ""}
                </span>
              ) : null}
            </div>

            {isLoading ? (
              <div className="stack">
                {[0, 1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-20 w-full" />
                ))}
              </div>
            ) : isError ? (
              <EmptyState
                icon={<AlertCircle className="h-12 w-12" />}
                title="Erro ao buscar"
                description={(error as Error)?.message ?? "Tente novamente"}
              />
            ) : results.length === 0 ? (
              <EmptyState
                icon={<Search className="h-12 w-12" />}
                title="Nenhum resultado encontrado"
                description={
                  allResults.length > 0
                    ? "Ajuste os filtros ou limpe-os para ver todos os resultados."
                    : `Sem matches para "${q}". Tente outro termo.`
                }
              />
            ) : (
              <div className="cards">
                {results.map((r) => (
                  <ResultCard key={`${r.channel_slug}-${r.sku}`} result={r} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ResultCard({ result }: { result: SearchResult }): JSX.Element {
  return (
    <Link to={`/canais/${result.channel_slug}/produtos/${result.sku}`} style={{ textDecoration: "none", color: "inherit" }}>
      <Card className="cluster" style={{ minWidth: "18rem" }}>
        {result.cover_url ? (
          <Thumb src={result.cover_url} alt="" size="2.75rem" />
        ) : (
          <span className="thumb" style={{ width: "2.75rem", height: "2.75rem", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <ImageOff className="icon muted" aria-hidden />
          </span>
        )}
        <div className="grow">
          <div className="cluster" style={{ fontSize: "var(--fs-sm)" }}>
            <span className="mono muted">{result.sku}</span>
            <Badge variant={STATUS_VARIANT[result.status] ?? "draft"}>{pubStatusLabel(result.status)}</Badge>
            {result.archived ? <ArchivedBadge /> : null}
            <span className="chip">{result.channel_name ?? result.channel_slug}</span>
          </div>
          <div className="clamp-2" style={{ marginTop: ".125rem", fontWeight: "var(--fw-black)" }}>
            {result.name}
          </div>
        </div>
      </Card>
    </Link>
  );
}
